import ChalkySticks from '@chalkysticks/sdk';

/**
 * Accepted MIME types for table scan images
 *
 * @type string[]
 */
const ACCEPTED_IMAGE_TYPES: string[] = ['image/gif', 'image/jpeg', 'image/png', 'image/webp'];

/**
 * Represents a single ball ready for placement on the pad table
 *
 * @interface ITableScanBall
 */
export interface ITableScanBall {
	type: string;
	x: number;
	y: number;
}

/**
 * Result returned from a successful table scan
 *
 * @interface ITableScanResult
 */
export interface ITableScanResult {
	balls: ITableScanBall[];
	originalDetectionCount: number;
}

/**
 * Service for scanning pool table images and converting detected ball
 * positions into normalized coordinates suitable for the pad diagram.
 *
 * Handles image validation, API upload, inference processing via
 * ImageAnalyzer, and the coordinate rotation transform.
 *
 * @author Matt Kenefick <matt@chalkysticks.com>
 * @package App/Service
 * @project ChalkySticks App
 */
export class TableScan {
	/**
	 * Validates that the provided file is an accepted image type.
	 *
	 * @param File file
	 * @return boolean
	 */
	public isValidImage(file: File): boolean {
		return ACCEPTED_IMAGE_TYPES.includes(file.type);
	}

	/**
	 * Scans a table image and returns normalized ball positions ready for
	 * placement on the pad diagram. Dynamically imports sdk-inference to
	 * keep it in a separate webpack chunk.
	 *
	 * Steps:
	 *   1. Validate the file is an image
	 *   2. Upload via FormData to the table-scan-full API endpoint
	 *   3. Process detections through ImageAnalyzer.processDetections()
	 *   4. Rotate coordinates (x/y swap + 1-x transform)
	 *   5. Map 'unknown' ball types to 'ghost'
	 *
	 * @param File imageFile
	 * @return Promise<ITableScanResult>
	 * @throws Error if the file is not a valid image
	 * @throws Error if the API request fails
	 * @throws Error if no transformed data is returned
	 */
	public async scan(imageFile: File): Promise<ITableScanResult> {
		// Validate image type
		if (!this.isValidImage(imageFile)) {
			throw new Error('Invalid file type. Please select an image file (JPEG, PNG, GIF, or WebP).');
		}

		// Dynamically import sdk-inference for code splitting
		const Inference = await import('@chalkysticks/sdk-inference');

		// Build the API endpoint URL from SDK constants
		const apiBaseUrl = ChalkySticks.Core.Constants.API_URL;
		const endpoint = `${apiBaseUrl}/data/table-scan-full`;

		// Build form data with the image
		const formData = new FormData();
		formData.append('image', imageFile);

		// Upload to the scan API
		const response = await fetch(endpoint, {
			body: formData,
			method: 'POST',
		});

		if (!response.ok) {
			throw new Error(`Table scan request failed with status ${response.status}`);
		}

		const detections = await response.json();

		// Process detections through the ImageAnalyzer pipeline
		const analyzer = new Inference.ImageAnalyzer({
			autoTransform: true,
		});

		const frame = await analyzer.processDetections(detections, {
			height: 2100,
			width: 1000,
		});

		// Verify we received transformed data
		if (!frame.transformedData) {
			throw new Error(
				'Table scan did not return transformed ball positions. The image may not contain a recognizable table layout.',
			);
		}

		// Transform balls: rotate coordinates and normalize ball types
		const balls: ITableScanBall[] = frame.transformedData.balls.map((ball: any) => {
			const normalizedX = ball.normalizedX as number;
			const normalizedY = ball.normalizedY as number;
			const rotatedX = normalizedY;
			const rotatedY = 1 - normalizedX;
			let ballType = ball.ballType;

			ballType = ballType.replace(/unknown|ghost|none/i, 'ghost');
			ballType = ballType.replace(/^0$/, 'cue');

			return {
				type: ballType,
				x: rotatedX,
				y: rotatedY,
			};
		});

		return {
			balls: balls,
			originalDetectionCount: detections.length,
		};
	}
}

// Singleton
export default new TableScan();
